/**
 * PriorityQueue - six-item ranked grid with italic dropcap numerals.
 *
 * Visual reference: 04-hybrid-recommended.html .queue-section block.
 * The numeral color is the SEVERITY signal, not decoration: red for crit,
 * sand for high, blue-deep for med, slate-rule for routine. Filter chips at
 * top right narrow the list to a single pillar.
 *
 * Data: usePriorityActions({ tenantId, maxCount, pillar }). Filter chips
 * refetch with the selected pillar so each pillar view shows up to 6 ranked
 * actions for that pillar (rather than narrowing a single all-pillar fetch
 * which could be empty for a given pillar). See
 * .agents/Tasks/High Priority/operations-priority-alerts-curation.md §4.5.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import type { Pillar, PriorityAction, Severity } from '@/lib/posture-brief'
import ScoreDeltaChip from './ScoreDeltaChip'
import PriorityPill from './PriorityPill'
import LearnMoreLink from './LearnMoreLink'

type Filter = 'all' | Pillar

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: 'all',        label: 'All pillars' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'bpa',        label: 'Best Practice' },
  { id: 'identity',   label: 'Identity' },
]

const PILLAR_LABEL: Record<Pillar, string> = {
  compliance: 'Compliance',
  bpa:        'Best Practice',
  identity:   'Identity',
}

const PILLAR_TEXT_CLASS: Record<Pillar, string> = {
  compliance: 'text-primary-700 dark:text-primary-400',
  bpa:        'text-secondaryBlue-700 dark:text-secondaryBlue-300',
  identity:   'text-secondarySand-700 dark:text-secondarySand-300',
}

const SEVERITY_NUMERAL_COLOR: Record<Severity, string> = {
  crit:    '#E57373',
  high:    'rgb(var(--color-secondary-sand-500))',
  med:     'rgb(var(--color-secondary-blue-500))',
  low:     'rgb(var(--color-primary-500))',
  routine: 'rgb(var(--color-dark-600))',
}

export interface PriorityQueueProps {
  /** Active tenant. When undefined the hook stays idle and the queue
   *  renders its empty state without firing a request. */
  tenantId: string | undefined
}

export default function PriorityQueue({ tenantId }: PriorityQueueProps) {
  const [filter, setFilter] = useState<Filter>('all')
  const { actions, isLoading, error } = usePriorityActions({
    tenantId,
    maxCount: 6,
    pillar: filter,
  })

  // Server already returns the right slice for the chosen filter, so we
  // render the response verbatim. No additional client-side narrowing.
  const rows = actions

  const headline =
    error
      ? "Couldn't reach the queue. We'll retry."
      : rows.length === 0
        ? filter === 'all'
          ? 'No outstanding moves this week.'
          : 'No outstanding moves in this pillar.'
        : `${numberAsWord(rows.length)} concrete moves, ranked by impact.`

  return (
    <section
      aria-labelledby="queue-heading"
      className="
        mb-9 rounded-3xl border border-dark-700 bg-dark-800 p-9 pb-3
        shadow-[0_1px_2px_rgba(33,41,56,0.04),0_8px_24px_rgba(33,41,56,0.04)]
      "
    >
      <header
        className="
          mb-7 flex flex-col gap-4 border-b border-dark-700 pb-6
          sm:flex-row sm:items-end sm:justify-between
        "
      >
        <div>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            What to do this week
          </div>
          <h2
            id="queue-heading"
            className="font-display text-[26px] font-extrabold leading-[1.05] tracking-[-0.02em] text-slate-100 dark:text-slate-100 sm:text-[32px]"
          >
            {headline}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]">
          <span className="text-slate-500">Filter:</span>
          {FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={clsx(
                'rounded-full border px-3.5 py-1.5 transition-colors',
                f.id === filter
                  ? 'border-[#212938] bg-[#212938] text-white'
                  : 'border-dark-600 bg-dark-900 text-slate-100 hover:border-primary-500 dark:text-slate-100',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <QueueSkeletonGrid count={6} />
      ) : error ? (
        <p
          className="
            mx-2 mb-8 rounded-lg border border-dark-700 bg-dark-900 px-4 py-3
            text-[13px] italic leading-snug text-slate-400
          "
          role="status"
        >
          Couldn't load priority queue. Refresh to retry.
        </p>
      ) : rows.length === 0 ? (
        <p className="px-2 pb-8 text-[14px] text-slate-500">
          {filter === 'all'
            ? 'No outstanding moves this week. The queue refills as new findings arrive.'
            : 'Nothing flagged for this pillar right now. Switch filters to see other priorities.'}
        </p>
      ) : (
        <ol
          className="
            grid grid-cols-1 gap-0 lg:grid-cols-2
          "
        >
          {rows.map((action, idx) => (
            <QueueRow
              key={action.id}
              action={action}
              displayRank={idx + 1}
              isEven={idx % 2 === 1}
            />
          ))}
        </ol>
      )}
    </section>
  )
}

/**
 * Skeleton grid shown while the queue is loading. Mirrors the 2-column
 * QueueRow chrome (rank numeral + title + meta line) so layout doesn't
 * shift on first paint.
 */
function QueueSkeletonGrid({ count }: { count: number }) {
  return (
    <ol
      className="grid grid-cols-1 gap-0 lg:grid-cols-2"
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: count }).map((_, idx) => (
        <li
          key={idx}
          className={clsx(
            'grid grid-cols-[56px_1fr_auto] items-center gap-5 border-b border-dark-700 py-5 pr-6',
            idx % 2 === 1 && 'lg:border-l lg:border-dark-700 lg:pl-7',
          )}
        >
          <span className="block h-7 w-9 rounded bg-white/[0.06] animate-pulse" />
          <span className="block">
            <span className="block h-4 w-3/4 rounded bg-white/[0.06] animate-pulse" />
            <span className="mt-2 block h-2.5 w-1/2 rounded bg-white/[0.04] animate-pulse" />
          </span>
          <span className="block h-3 w-3 rounded bg-white/[0.04] animate-pulse" />
        </li>
      ))}
    </ol>
  )
}

function QueueRow({
  action,
  displayRank,
  isEven,
}: {
  action: PriorityAction
  displayRank: number
  isEven: boolean
}) {
  return (
    <li
      className={clsx(
        isEven && 'lg:border-l lg:border-dark-700',
      )}
    >
      <Link
        to={action.href}
        className={clsx(
          // Right column gets a left rule + extra left-padding to mirror the
          // mockup's 2-column composition.
          'group grid grid-cols-[56px_1fr_auto] items-center gap-5 border-b border-dark-700 py-5 pr-6',
          'transition-[padding,background] duration-200',
          'hover:bg-dark-900/50 hover:pl-7 dark:hover:bg-white/[0.025]',
          isEven && 'lg:pl-7',
        )}
      >
        <span
          className="text-center font-display italic font-extrabold leading-none tabular-nums"
          style={{
            fontSize: 38,
            letterSpacing: '-0.03em',
            color: SEVERITY_NUMERAL_COLOR[action.severity],
          }}
          aria-label={`Rank ${displayRank}`}
        >
          {String(displayRank).padStart(2, '0')}
        </span>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[16px] font-bold leading-tight text-slate-100 dark:text-slate-100">
              {action.title}
            </div>
            <PriorityPill severity={action.severity} pillar={action.pillar} />
            <ScoreDeltaChip delta={action.guardianScoreDelta} />
            <LearnMoreLink url={action.learnMoreUrl} size="sm" />
          </div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            <span className={clsx('font-bold', PILLAR_TEXT_CLASS[action.pillar])}>
              {PILLAR_LABEL[action.pillar]}
            </span>
            {action.meta.split('/').map((segment, i) => (
              <span key={i}>
                <span aria-hidden className="mx-2 text-dark-600">
                  /
                </span>
                {segment.trim()}
              </span>
            ))}
          </div>
        </div>

        <ArrowRightIcon
          className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-1.5 group-hover:text-slate-100"
          aria-hidden
        />
      </Link>
    </li>
  )
}

function numberAsWord(n: number): string {
  switch (n) {
    case 1: return 'One'
    case 2: return 'Two'
    case 3: return 'Three'
    case 4: return 'Four'
    case 5: return 'Five'
    case 6: return 'Six'
    case 7: return 'Seven'
    case 8: return 'Eight'
    default: return String(n)
  }
}
